// ============================================================================
// PollMethodCard — карточка методики (единый вид для психолога и страницы
// «Тесты и анкеты»). БЕЗ таблиц и окон: карточка одинаково читаема на телефоне
// и на десктопе, действия переносятся на новую строку.
// ============================================================================

import type { ReactNode } from 'react';
import { ChevronRight, ClipboardList, ListChecks } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  METHOD_CATEGORY_HINT, METHOD_CATEGORY_LABEL, methodMeta,
  type PollMethodCategory, type PollMethodItem,
} from '@/components/polls/pollMethods';

const CATEGORY_CLASS: Record<PollMethodCategory, string> = {
  screening: 'border-primary/30 bg-primary/10 text-primary',
  scales: 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300',
  questionnaire: 'border-border bg-muted/60 text-muted-foreground',
  external: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  other: 'border-border bg-muted/40 text-muted-foreground',
};

export function MethodCategoryChip({ category }: { category: PollMethodCategory }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium',
        CATEGORY_CLASS[category],
      )}
      title={METHOD_CATEGORY_HINT[category]}
    >
      {METHOD_CATEGORY_LABEL[category]}
    </span>
  );
}

export interface PollMethodCardProps {
  method: PollMethodItem;
  onOpen?: (method: PollMethodItem) => void;
  /** Дополнительные бейджи (результат, назначение). */
  badges?: ReactNode;
  /** Кнопки действий карточки. */
  actions?: ReactNode;
  selected?: boolean;
}

export function PollMethodCard({ method, onOpen, badges, actions, selected }: PollMethodCardProps) {
  const open = onOpen ? () => onOpen(method) : undefined;
  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-lg border bg-card p-3 transition-colors',
        selected && 'border-primary ring-1 ring-primary/30',
        open && 'hover:bg-accent/30',
      )}
      onClick={open}
      onKeyDown={(e) => {
        if (!open) return;
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
      role={open ? 'button' : undefined}
      tabIndex={open ? 0 : undefined}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium">{method.synonym}</span>
            {open && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
          </div>
          {method.name && method.name !== method.synonym && (
            <div className="truncate text-[10px] text-muted-foreground">{method.name}</div>
          )}
        </div>
        <MethodCategoryChip category={method.category} />
      </div>

      {method.description && (
        <div className="line-clamp-2 text-[11px] text-muted-foreground" title={method.description}>
          {method.description}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-muted-foreground">
        {method.questionCount === 0 ? (
          <span className="inline-flex items-center gap-1">
            <ClipboardList className="h-3 w-3" />{methodMeta(method)}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1">
            <ListChecks className="h-3 w-3" />{methodMeta(method)}
          </span>
        )}
        {method.status && method.status !== 'open' && (
          <span className="rounded-full border px-1.5 py-0.5">{method.status === 'closed' ? 'закрыта' : method.status}</span>
        )}
      </div>

      {badges && <div className="flex flex-wrap items-center gap-1">{badges}</div>}

      {actions && (
        <div className="mt-auto flex flex-wrap gap-1.5 pt-1" onClick={(e) => e.stopPropagation()}>
          {actions}
        </div>
      )}
    </div>
  );
}
